<template>
  <div :class="classes">
    <label v-if="title" :class="$style.title" :for="id">
      {{ title }}
    </label>

    <div
      :class="[$style.control, props.rows && $style.autoheight]"
      @click="!disabled && $inputRef?.focus()"
    >
      <button
        v-if="iconLeft"
        :class="$style.iconButton"
        type="button"
        :aria-label="iconLeftAriaLabel"
        :disabled="!hasClickIconLeftListener"
        @click.stop="$emit('clickIconLeft', $event)"
      >
        <ui-icon :name="iconLeft" />
      </button>

      <span v-if="prefix" :class="$style.affix">{{ prefix }}</span>

      <ui-textarea
        v-if="rows"
        :id
        ref="$inputRef"
        v-model="model"
        :class="$style.input"
        :placeholder
        :disabled
        :readonly
        :required
        :autofocus
        :debounce
        :lazy
        :mask
        :maxlength
        :autocomplete
        :rows
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />
      <ui-input
        v-else
        :id
        ref="$inputRef"
        v-model="model"
        :class="[$style.input]"
        :style="{ color: textColor, textAlign: textFieldAlign }"
        :type
        :placeholder
        :disabled
        :readonly
        :required
        :autofocus
        :debounce
        :lazy
        :mask
        :maxlength
        :autocomplete
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />

      <span v-if="postfix" :class="$style.affix">{{ postfix }}</span>

      <button
        v-if="clearable && model && !(disabled || readonly)"
        :class="$style.iconButton"
        type="button"
        :aria-label="clearButtonAriaLabel"
        @click="model = ''"
      >
        <ui-icon name="x" />
      </button>

      <button
        v-if="iconRight"
        :class="$style.iconButton"
        type="button"
        :aria-label="iconRightAriaLabel"
        :disabled="!hasClickIconRightListener"
        @click.stop="$emit('clickIconRight', $event)"
      >
        <ui-icon :name="iconRight" />
      </button>
    </div>

    <p v-if="description" :class="$style.description">
      {{ description }}
    </p>

    <transition :name="$style.status">
      <p
        v-if="props.error && typeof props.error === 'string'"
        :class="[$style.status, $style.error]"
      >
        <ui-icon :class="$style.statusIcon" name="alert-triangle-filled" />
        <span :class="$style.statusText">{{ props.error }}</span>
      </p>
    </transition>

    <transition :name="$style.status">
      <p
        v-if="props.success && typeof props.success === 'string'"
        :class="[$style.status, $style.success]"
      >
        <ui-icon :class="$style.statusIcon" name="check-circle-filled" />
        <span :class="$style.statusText">{{ props.success }}</span>
      </p>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import {
  useCssModule,
  useId,
  useTemplateRef,
  ref,
  computed,
  getCurrentInstance
} from 'vue'
import type { FactoryArg } from 'imask'
import { useLocaleMessage } from '@/ui-core/composables/use-locale-message'
import UiIcon from '../icon/index.vue'
import UiInput from './input.vue'
import UiTextarea from './textarea.vue'
import type { Theme, Size } from './consts'
import type { FieldInputRef } from './types'

defineOptions({
  name: 'UiField'
})

const props = withDefaults(
  defineProps<{
    title?: string
    titleUppercase?: boolean
    theme?: Theme
    size?: Size
    type?: string
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    autofocus?: boolean
    clearable?: boolean
    debounce?: number
    lazy?: boolean
    mask?: FactoryArg
    maxlength?: number
    autocomplete?: string
    iconLeft?: string
    iconLeftAriaLabel?: string
    prefix?: string
    placeholder?: string
    postfix?: string
    iconRight?: string
    iconRightAriaLabel?: string
    description?: string
    error?: string | boolean
    success?: string | boolean
    rows?: number
    textColor?: string
    textFieldAlign?: 'left' | 'center'
  }>(),
  {
    title: '',
    titleUppercase: false,
    theme: 'default',
    size: 'default',
    type: 'text',
    disabled: false,
    readonly: false,
    required: false,
    autofocus: false,
    clearable: false,
    debounce: undefined,
    lazy: false,
    mask: undefined,
    maxlength: undefined,
    autocomplete: undefined,
    iconLeft: '',
    iconLeftAriaLabel: undefined,
    prefix: '',
    placeholder: undefined,
    postfix: '',
    iconRight: '',
    iconRightAriaLabel: undefined,
    description: '',
    error: false,
    success: false,
    rows: undefined,
    textColor: undefined,
    textFieldAlign: 'left'
  }
)

defineEmits<{
  clickIconLeft: [evt: MouseEvent]
  clickIconRight: [evt: MouseEvent]
}>()

const model = defineModel<string>()

const cssModule = useCssModule()
const id = useId()
const $inputRef = useTemplateRef<FieldInputRef>('$inputRef')

const clearButtonAriaLabel = useLocaleMessage('fieldClearButtonAriaLabel')

const isInputFocused = ref(false)

const hasClickIconLeftListener = computed(() =>
  Boolean(getCurrentInstance()?.vnode.props?.onClickIconLeft)
)
const hasClickIconRightListener = computed(() =>
  Boolean(getCurrentInstance()?.vnode.props?.onClickIconRight)
)

const classes = computed(() => [
  cssModule.field,
  cssModule['theme-' + props.theme],
  cssModule['size-' + props.size],
  {
    [cssModule.titleUppercase]: props.titleUppercase,
    [cssModule.focused]: isInputFocused.value,
    [cssModule.disabled]: props.disabled,
    [cssModule.error]: props.error,
    [cssModule.success]: props.success
  }
])
</script>

<style lang="scss" module>
@use 'theme';
@use 'size';

.field {
  display: flex;
  flex-direction: column;
  gap: var-default(--field-gap);
  align-items: flex-start;

  /* Size */
  @include size.add(s);
  @include size.add(l);

  &.titleUppercase {
    .title {
      text-transform: uppercase;
    }
  }

  &.focused {
    .title {
      color: var-default(--field-focus-title-color);
    }

    .control {
      border-color: var-default(--field-focus-control-border-color);
      color: var-default(--field-focus-control-color);
      background: var-default(--field-focus-control-bg);
    }

    .input {
      &::placeholder {
        color: var-default(--field-focus-placeholder-color);
      }
    }
  }

  &.success {
    .control {
      border-color: var-default(--field-success-control-border-color);
    }
  }

  &.error {
    .control {
      border-color: var-default(--field-error-control-border-color);
    }
  }

  &.disabled {
    .title {
      color: var-default(--field-disabled-title-color);
    }

    .control {
      border-color: var-default(--field-disabled-control-border-color);
      color: var-default(--field-disabled-control-color);
      background: var-default(--field-disabled-control-bg);
      pointer-events: none;
    }

    .input {
      &::placeholder {
        color: var-default(--field-disabled-placeholder-color);
      }
    }

    .description {
      color: var-default(--field-disabled-description-color);
    }
  }
}

.title {
  font-family: var-default(--field-title-font-family);
  font-size: var-default(--field-title-font-size);
  font-weight: var-default(--field-title-font-weight);
  line-height: var-default(--field-title-line-height);
  letter-spacing: var-default(--field-title-letter-spacing);
  color: var-default(--field-title-color);
  user-select: none;
  transition: color var(--root-field-transition-duration);
}

.control {
  border-radius: var-default(--field-control-border-radius);
  border: var-default(--field-control-border-width) solid
    var-default(--field-control-border-color);
  padding: 0 var-default(--field-control-padding-x);
  height: var-default(--field-control-height);
  color: var-default(--field-control-color);
  background: var-default(--field-control-bg);
  user-select: none;
  cursor: text;
  align-self: stretch;
  display: flex;
  gap: var-default(--field-control-gap);
  align-items: center;
  transition-property: border-color, color, background;
  transition-duration: var(--root-field-transition-duration);

  &:hover {
    border-color: var-default(--field-hover-control-border-color);
    color: var-default(--field-hover-control-color);
    background: var-default(--field-hover-control-bg);

    .input {
      &::placeholder {
        color: var-default(--field-hover-placeholder-color);
      }
    }
  }
}

.control.autoheight {
  height: auto;
  padding: var-default(--field-control-padding-x)
    var-default(--field-control-padding-x);
}

.input {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  font-family: var-default(--field-input-font-family);
  font-size: var-default(--field-input-font-size);
  font-weight: var-default(--field-input-font-weight);
  line-height: var-default(--field-input-line-height);
  letter-spacing: var-default(--field-input-letter-spacing);

  &::placeholder {
    color: var-default(--field-placeholder-color);
    transition: color var(--root-field-transition-duration);
  }
}

.iconButton {
  width: var-default(--field-control-icon-height);
  height: var-default(--field-control-icon-height);

  &:disabled {
    pointer-events: none;
  }

  svg {
    width: 100%;
    height: 100%;
  }
}

.affix {
  font-family: var-default(--field-affix-font-family);
  font-size: var-default(--field-affix-font-size);
  font-weight: var-default(--field-affix-font-weight);
  line-height: var-default(--field-affix-line-height);
  letter-spacing: var-default(--field-affix-letter-spacing);
}

.description {
  font-family: var-default(--field-description-font-family);
  font-size: var-default(--field-description-font-size);
  font-weight: var-default(--field-description-font-weight);
  line-height: var-default(--field-description-line-height);
  letter-spacing: var-default(--field-description-letter-spacing);
  color: var-default(--field-description-color);
}

.status {
  &:global(-enter-active),
  &:global(-leave-active) {
    transition: opacity var(--root-field-transition-duration);
  }

  &:global(-enter-from),
  &:global(-leave-to) {
    opacity: 0;
  }

  &.error {
    color: var-default(--field-error-status-color);

    .statusIcon {
      color: var-default(--field-error-status-icon-color);
    }
  }

  &.success {
    color: var-default(--field-success-status-color);

    .statusIcon {
      color: var-default(--field-success-status-icon-color);
    }
  }
}

.statusIcon {
  margin-right: var-default(--field-status-gap);
  width: var-default(--field-status-icon-height);
  height: var-default(--field-status-icon-height);
  vertical-align: middle;
}

.statusText {
  vertical-align: middle;
  font-family: var-default(--field-status-font-family);
  font-size: var-default(--field-status-font-size);
  font-weight: var-default(--field-status-font-weight);
  line-height: var-default(--field-status-line-height);
  letter-spacing: var-default(--field-status-letter-spacing);
}
</style>
